<template>
  <div class="community-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('communityManage.page.title')" :subtitle="$t('communityManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddCommunityModal">
          {{ $t('communityManage.list.addCommunity') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="communityManageInfo.conditions.communityId" clearable class="w-full"
            :placeholder="$t('communityManage.search.communityId')" @keyup.enter.native="_queryCommunityMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="communityManageInfo.conditions.name" clearable class="w-full"
            :placeholder="$t('communityManage.search.name')" @keyup.enter.native="_queryCommunityMethod" />
        </el-col>
        <el-col :xs="24" :sm="24" :md="8" :lg="12">
          <area-select ref="areaSelect" @selectArea="selectArea" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryCommunityMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetCommunityMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('communityManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button size="mini" icon="el-icon-document" @click="toDoc('/pages/hc/addCommunity_cn.html')">
            {{ $t('common.document') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="communityManageInfo.communitys" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('communityManage.page.empty')" @retry="refreshList">
        <el-table-column prop="communityId" :label="$t('communityManage.table.communityId')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.communityId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('communityManage.table.name')" align="left" min-width="140"
          show-overflow-tooltip />
        <el-table-column prop="storeName" :label="$t('communityManage.table.storeName')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.storeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="nearbyLandmarks" :label="$t('communityManage.table.nearbyLandmarks')" align="left"
          min-width="200" show-overflow-tooltip />
        <el-table-column prop="cityName" :label="$t('communityManage.table.area')" align="center" width="180"
          show-overflow-tooltip />
        <el-table-column prop="createTime" :label="$t('communityManage.table.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <!-- 动态扩展列：属性规格表（building_community_attr） -->
        <el-table-column v-for="(item, index) in communityManageInfo.listColumns" :key="index" :label="item.specName"
          align="center" min-width="120">
          <template slot-scope="scope">
            {{ scope.row.listValues[index] || '-' }}
          </template>
        </el-table-column>
        <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
        <el-table-column prop="stateName" :label="$t('communityManage.table.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="_openEditCommunityModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" @click="_sendCommunityIot(scope.row)">
              {{ $t('communityManage.table.syncIot') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteCommunityModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('communityManage.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 组件 -->
    <add-community ref="addCommunity" @listData="_queryCommunityMethod" />
    <edit-community ref="editCommunity" @listData="_queryCommunityMethod" />
    <delete-community ref="deleteCommunity" @listData="_queryCommunityMethod" />
    <community-data-to-iot ref="communityDataToIot" />
  </div>
</template>

<script>
import { listCommunitys } from '@/api/community/communityManageApi'
import AreaSelect from '@/components/community/areaSelect'
import AddCommunity from '@/components/community/addCommunity'
import EditCommunity from '@/components/community/editCommunity'
import DeleteCommunity from '@/components/community/deleteCommunity'
import CommunityDataToIot from '@/components/community/communityDataToIot'
import { getAttrSpecList } from '@/api/dev/attrSpecApi'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunityManageList',
  components: {
    AreaSelect,
    AddCommunity,
    EditCommunity,
    DeleteCommunity,
    CommunityDataToIot,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityManageInfo: {
        communitys: [],
        storeTypeCd: '',
        conditions: {
          name: '',
          cityCode: '',
          communityId: ''
        },
        listColumns: []
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this._initMethod()
  },
  methods: {
    async _initMethod() {
      await this._getColumns()
      this._listCommunitys(this.pagination.current, this.pagination.size)
    },
    _listCommunitys(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      const params = {
        ...this.communityManageInfo.conditions,
        page,
        row: size
      }
      listCommunitys(params).then(res => {
        this.communityManageInfo.total = res.total
        this.communityManageInfo.records = res.records
        this.communityManageInfo.communitys = res.communitys || []
        this.dealCommunityAttr(this.communityManageInfo.communitys)
        this.pagination.total = res.total || 0
      }).catch(error => {
        console.error('请求失败处理', error)
        this.hasError = true
        this.errorMessage = this.$t('communityManage.page.loadFailed')
      }).finally(() => {
        this.loading = false
      })
    },
    /**
     * 刷新当前页（工具条刷新按钮 / 错误态重试）
     */
    refreshList() {
      this._listCommunitys(this.pagination.current, this.pagination.size)
    },
    selectArea(area) {
      this.communityManageInfo.conditions.cityCode = area.selectArea
    },
    _openAddCommunityModal() {
      this.$refs.addCommunity.openModal()
    },
    _openEditCommunityModel(community) {
      this.$refs.editCommunity.openModal(community)
    },
    _openDeleteCommunityModel(community) {
      this.$refs.deleteCommunity.openModal(community)
    },
    _queryCommunityMethod() {
      this.pagination.current = 1
      this._listCommunitys(this.pagination.current, this.pagination.size)
    },
    _resetCommunityMethod() {
      this.communityManageInfo.conditions = {
        name: '',
        cityCode: '',
        communityId: ''
      }
      // 同步清空地区级联组件的内部选中态，避免条件与控件显示不一致
      if (this.$refs.areaSelect && this.$refs.areaSelect.clearArea) {
        this.$refs.areaSelect.clearArea()
      }
      this.pagination.current = 1
      this._listCommunitys(this.pagination.current, this.pagination.size)
    },
    _sendCommunityIot(community) {
      this.$refs.communityDataToIot.openModal(community)
    },
    dealCommunityAttr(communitys) {
      communitys.forEach(item => {
        this._getColumnsValue(item)
      })
    },
    _getColumnsValue(community) {
      community.listValues = []
      if (!community.communityAttrDtos || community.communityAttrDtos.length < 1) {
        this.communityManageInfo.listColumns.forEach(() => {
          community.listValues.push('')
        })
        return
      }
      this.communityManageInfo.listColumns.forEach(column => {
        let tmpValue = ''
        community.communityAttrDtos.forEach(attrItem => {
          if (column.specCd === attrItem.specCd) {
            tmpValue = attrItem.value
          }
        })
        community.listValues.push(tmpValue)
      })
    },
    async _getColumns() {
      const { data } = await getAttrSpecList({
        page: 1,
        row: 100,
        tableName: 'building_community_attr'
      })
      this.communityManageInfo.listColumns = []
      ;(data || []).forEach(item => {
        if (item.listShow === 'Y') {
          this.communityManageInfo.listColumns.push({
            specCd: item.specCd,
            specName: item.specName
          })
        }
      })
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listCommunitys(this.pagination.current, val)
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listCommunitys(val, this.pagination.size)
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    /**
     * 状态语义类型（小区）：1000 待审核 / 1100 审核通过 / 1200 拒绝审核
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'community',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.community-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}

/* 地区级联：组件内部为行内固定宽度，这里统一改为等宽三列 */
::v-deep .area-select {
  display: flex;
  gap: var(--ds-spacing-sm);

  .el-select {
    flex: 1 1 0;
    min-width: 0;
    width: auto !important;
    margin-right: 0 !important;
  }
}
</style>
